import { ReportElement } from './report-element';
import { ReportUnits } from './report-units';
import { ReportFonts } from './report-fonts';
import { TemplateSignature, ElementLocationInSection } from './report-template';
import { ElementType } from './element-type';
import { ReportPreviewMetadata, PropertyPreviewMetadata, FieldType, UpdateMetadata } from '../_services/report-template-design.service';
import { Subscription } from 'rxjs';
import { LangUpdateService } from '../_services/lang-update.service';
import { TranslateService } from '@ngx-translate/core';
import { BORDER_STYLE } from '../app.constants';

export class ReportSignature extends ReportElement {

  public top: string;
  public left: string;
  public zIndex: number;
  public width: string;
  public height: string;
  public borderStyle: string;
  public borderColor: string;
  public borderWidth: string;
  public reservedFor: string;

  private units: ReportUnits;

  private baseDef: TemplateSignature;
  private readonly metaId = 'id';
  private readonly metaTop = 'y';
  private readonly metaLeft = 'x';
  private readonly metaWidth = 'width';
  private readonly metaHeight = 'height';
  private readonly metaZIndex = 'zIndex';
  private readonly metaBorderStyle = 'borderStyle';
  private readonly metaBorderColor = 'borderColor';
  private readonly metaBorderWidth = 'borderWidth';
  private readonly metaReservedFor = 'reservedFor';

  constructor(index: number, units: ReportUnits, fonts: ReportFonts,
    protected translate: TranslateService,
    protected langService: LangUpdateService,
    templateSignature?: TemplateSignature) {

    super(ElementType.Signature, index, !(templateSignature == null),translate, langService, templateSignature ? templateSignature.id : null);

    this.units = units;

    if (templateSignature) {
      this.baseDef = templateSignature;
      this.baseDef.id = this.baseDef.id ? this.baseDef.id : this.generateUUID();
    } else {
      this.baseDef = {
        id: this.generateUUID(),
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        zIndex: 0,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#a0a0a0',
        reservedFor: ''
      };
    }
    this.zIndex = this.baseDef.zIndex;

    this.top = this.units.concatenate('sizeUnit', this.baseDef.y);
    this.left = this.units.concatenate('sizeUnit', this.baseDef.x);

    this.width = this.getWidth();
    this.height = this.getHeight();
    this.borderWidth = this.units.concatenate('sizeUnit', this.baseDef.borderWidth);
    this.borderStyle = this.baseDef.borderStyle;
    this.borderColor = this.baseDef.borderColor;
    this.reservedFor = this.baseDef.reservedFor;

    this.setChainUpdate();
    this.setPreviewMetadata();
  }

  public setDrawData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
    const width = this.units.fromPx('sizeUnit', metadata.widthPx) < 80 ? 80 : this.units.fromPx('sizeUnit', metadata.widthPx);
    const height = this.units.fromPx('sizeUnit', metadata.heightPx) < 55 ? 55 : this.units.fromPx('sizeUnit', metadata.heightPx);
    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: width,
      oldValue: this.baseDef[this.metaWidth]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: height,
      oldValue: this.baseDef[this.metaHeight]
    });
  }
  public setDropData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
  }
  public setResizeData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaWidth]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaHeight]
    });
  }

  public getLeftPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaLeft]);
  }
  public getTopPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTop]);
  }
  public getWidthPx(): number {
    return this.units.toPx('sizeUnit', this.getWidthNum());
  }
  public getHeightPx(): number {
    return this.units.toPx('sizeUnit', this.getHeightNum());
  }

  public getTemplateJSON(): TemplateSignature {
    return this.baseDef;
  }

  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      {
        name: this.metaLeft, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Position'
      },
      {
        name: this.metaTop, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Position'
      },
      {
        name: this.metaBorderWidth, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Line'
      },
      {
        name: this.metaBorderColor, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Color',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Line'
      },
      {
        name: this.metaBorderStyle, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: BORDER_STYLE,
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Line'
      },
      {
        name: this.metaReservedFor, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'Inspector', value: 'Inspector' },
          { label: 'Reviewer', value: 'Reviewer' }
        ],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding'
      }
    ];
  }

  public setSectionInfo(id: string, stageName: string, location: ElementLocationInSection): void {
    this.baseDef.sectionId = id;
    this.baseDef.sectionStageTag = stageName;
    this.baseDef.elementLocationInSection = location;
  }

  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTop:
        this.baseDef[metadata.name] = metadata.value as number;
        this.top = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLeft:
        this.baseDef[metadata.name] = metadata.value as number;
        this.left = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaWidth:
        this.baseDef[metadata.name] = metadata.value as number;
        this.width = this.getWidth();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHeight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.height = this.getHeight();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaZIndex:
        this.baseDef[metadata.name] = metadata.value as number;
        this.zIndex = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderWidth:
        this.baseDef[metadata.name] = metadata.value as number;
        this.borderWidth = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderStyle:
        this.baseDef[metadata.name] = metadata.value as string;
        this.borderStyle = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderColor:
        this.baseDef[metadata.name] = metadata.value as string;
        this.borderColor = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaReservedFor:
        this.baseDef[metadata.name] = metadata.value as string;
        this.reservedFor = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
    }
  }

  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
    subscribers.push(
      this.units.onUpdateSizeUnit.subscribe((metadata: UpdateMetadata) => {
        this.updateProperties({
          id: this.getId(),
          name: this.metaTop,
          value: this.convertUnit(this.baseDef[this.metaTop], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaTop]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaLeft,
          value: this.convertUnit(this.baseDef[this.metaLeft], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaLeft]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaWidth,
          value: this.convertUnit(this.baseDef[this.metaWidth], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaWidth]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaHeight,
          value: this.convertUnit(this.baseDef[this.metaHeight], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaHeight]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaBorderWidth,
          value: this.convertUnit(this.baseDef[this.metaBorderWidth], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaBorderWidth]
        });
      })
    );
  }

  private getWidthNum(): number {
    return this.baseDef.width;
  }
  private getHeightNum(): number {
    return this.baseDef.height;
  }
  private getWidth(): string {
    return this.units.concatenate('sizeUnit', this.getWidthNum());
  }
  private getHeight(): string {
    return this.units.concatenate('sizeUnit', this.getHeightNum());
  }
}
